import { z } from 'zod';
import type { PropertyGroupResponse } from '@/shared/api';
import type { ObjectPatch } from './objectUpdateRequest';

// Grenzen wie im Server-Validator — ein Speichern soll nicht erst dort scheitern.
export const addressSchema = z.object({
  name: z.string().trim().min(2, 'Mindestens 2 Zeichen').max(120, 'Höchstens 120 Zeichen'),
  addressQuery: z.string().optional(),
  streetName: z.string().trim().min(2, 'Straße fehlt').max(200, 'Höchstens 200 Zeichen'),
  // Internationale Nummern wie 1a, 1 A oder 12-14 bleiben gültig.
  houseNumber: z.string().trim().min(1, 'Hausnummer fehlt').max(20, 'Höchstens 20 Zeichen'),
  postalCode: z.string().trim().min(2, 'PLZ fehlt').max(20, 'Höchstens 20 Zeichen'),
  city: z.string().trim().min(2, 'Ort fehlt').max(120, 'Höchstens 120 Zeichen'),
  countryCode: z.string().length(2),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  needsAddressReview: z.boolean(),
});

export type AddressValues = z.infer<typeof addressSchema>;

export function addressDefaults(group: PropertyGroupResponse): AddressValues {
  const { address } = group;
  return {
    name: group.name,
    addressQuery: address.street,
    streetName: address.streetName,
    houseNumber: address.houseNumber,
    postalCode: address.postalCode,
    city: address.city,
    countryCode: address.countryCode,
    latitude: address.latitude,
    longitude: address.longitude,
    needsAddressReview: group.needsAddressReview,
  };
}

const ADDRESS_FIELDS = [
  'streetName',
  'houseNumber',
  'postalCode',
  'city',
  'countryCode',
  'latitude',
  'longitude',
  'needsAddressReview',
] as const;

/**
 * Die Adresse fährt nur mit, wenn sie selbst geändert wurde. Ein neuer Name lässt Adresse, Pin und
 * Prüfstand so, wie sie auf dem Server gerade stehen.
 */
export function addressPatch(values: AddressValues, initial: AddressValues): ObjectPatch {
  const name = values.name.trim();
  if (ADDRESS_FIELDS.every((field) => values[field] === initial[field])) return { name };
  const streetName = values.streetName.trim();
  const houseNumber = values.houseNumber.trim();
  return {
    name,
    address: {
      street: `${streetName} ${houseNumber}`,
      streetName,
      houseNumber,
      postalCode: values.postalCode.trim(),
      city: values.city.trim(),
      countryCode: values.countryCode.toUpperCase(),
      latitude: values.latitude ?? null,
      longitude: values.longitude ?? null,
    },
    needsAddressReview: values.needsAddressReview,
  };
}

const CLOCK = /^\d{2}:\d{2}/;

export const stayTimesSchema = z.object({
  checkInTime: z.string().regex(CLOCK, 'Uhrzeit fehlt'),
  checkOutTime: z.string().regex(CLOCK, 'Uhrzeit fehlt'),
});

export type StayTimesValues = z.infer<typeof stayTimesSchema>;

/** Der Server liefert „HH:mm:ss"; die Sekunden sind für den Betreiber Rauschen. */
export const clockTime = (value: string | undefined) => (value ? value.slice(0, 5) : '');

export const stayTimesDefaults = (group: PropertyGroupResponse): StayTimesValues => ({
  checkInTime: clockTime(group.checkInTime),
  checkOutTime: clockTime(group.checkOutTime),
});

export const stayTimesPatch = (values: StayTimesValues): ObjectPatch => ({
  checkInTime: `${clockTime(values.checkInTime)}:00`,
  checkOutTime: `${clockTime(values.checkOutTime)}:00`,
});

export const ACCESS_CODE_MAX = 32;
